import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Text,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  Select,
  useToast,
  Spinner,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { 
  FiCalendar, 
  FiDownload, 
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiTrendingUp,
  FiUsers,
  FiClock
} from "react-icons/fi";
import { 
  useOvertimeRequests, 
  overtimeManagementUtils 
} from "../../../services/adminOvertimeService";

const OvertimeReports = () => {
  const [filters, setFilters] = useState({
    week_start: new Date().toISOString().split('T')[0],
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const toast = useToast();

  // Load overtime requests data for reports
  const {
    requestsData,
    requestsError,
    requestsIsLoading,
    refetchRequests
  } = useOvertimeRequests({ filters });

  // Filter reports based on search term
  const filteredReports = useMemo(() => {
    if (!requestsData?.length) return [];
    
    return requestsData.filter(report => 
      report.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [requestsData, searchTerm]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Weekly overtime report is being prepared for download.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!filteredReports.length) {
      return {
        totalEmployees: 0,
        totalHours: 0,
        averageHours: 0,
        totalRequests: 0
      };
    }

    const totalHours = filteredReports.reduce((sum, report) => sum + (report.hours_requested || 0), 0);
    const totalRequests = filteredReports.length;
    const uniqueEmployees = new Set(filteredReports.map(r => r.employee_id)).size;

    return {
      totalEmployees: uniqueEmployees,
      totalHours: totalHours,
      averageHours: totalRequests > 0 ? totalHours / totalRequests : 0,
      totalRequests: totalRequests
    };
  }, [filteredReports]);

  const getStatusBadge = (status) => {
    const colorScheme = overtimeManagementUtils.getRequestStatusColor(status);
    const displayName = overtimeManagementUtils.getRequestStatusDisplay(status);
    
    return (
      <Badge colorScheme={colorScheme} variant="subtle" size="sm">
        {displayName}
      </Badge>
    );
  };

  if (requestsIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4}>Loading overtime reports...</Text>
      </Box>
    );
  }

  if (requestsError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error Loading Reports</AlertTitle>
        <AlertDescription>
          {requestsError.message || "Failed to load overtime reports. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="blue.100"
                  color="blue.600"
                >
                  <FiUsers size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Employees</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {summaryStats.totalEmployees}
              </StatNumber>
              <StatHelpText>
                With overtime this week
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="green.100"
                  color="green.600"
                >
                  <FiClock size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Total Hours</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {overtimeManagementUtils.formatOvertimeHours(summaryStats.totalHours)}
              </StatNumber>
              <StatHelpText>
                Approved overtime
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="orange.100"
                  color="orange.600"
                >
                  <FiTrendingUp size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Average Hours</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {overtimeManagementUtils.formatOvertimeHours(summaryStats.averageHours)}
              </StatNumber>
              <StatHelpText>
                Per employee
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <HStack spacing={3} mb={2}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg="purple.100"
                  color="purple.600"
                >
                  <FiCalendar size={20} />
                </Box>
                <StatLabel color="gray.600" fontSize="sm">Total Requests</StatLabel>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
                {summaryStats.totalRequests}
              </StatNumber>
              <StatHelpText>
                This week
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Reports Tabs */}
      <Card>
        <CardBody>
          <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed">
            <TabList>
              <Tab>Weekly Reports</Tab>
              <Tab>Monthly Reports</Tab>
            </TabList>

            <TabPanels>
              {/* Weekly Reports Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {/* Filters and Actions */}
                  <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Text fontSize="lg" fontWeight="600" color="gray.800">
                      Weekly Overtime Reports
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        leftIcon={<FiRefreshCw />}
                        variant="outline"
                        size="sm"
                        onClick={refetchRequests}
                        isLoading={requestsIsLoading}
                      >
                        Refresh
                      </Button>
                      <Button
                        leftIcon={<FiDownload />}
                        variant="outline"
                        size="sm"
                        onClick={handleExport}
                      >
                        Export
                      </Button>
                    </HStack>
                  </Flex>

                  {/* Filters */}
                  <HStack spacing={4} wrap="wrap">
                    <Box position="relative" minW="300px">
                      <Input
                        placeholder="Search employees..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        pl={10}
                        pr={4}
                      />
                      <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                        <FiSearch size={16} />
                      </Box>
                    </Box>

                    <Input
                      type="date"
                      value={filters.week_start}
                      onChange={(e) => handleFilterChange('week_start', e.target.value)}
                      placeholder="Week Start"
                      minW="150px"
                    />
                  </HStack>

                  {/* Reports Table */}
                  {filteredReports.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text fontSize="lg" color="gray.500">
                        No overtime reports found
                      </Text>
                      <Text fontSize="sm" color="gray.400" mt={2}>
                        {searchTerm || filters.week_start 
                          ? "Try adjusting your search or filters" 
                          : "No overtime data for this week"}
                      </Text>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Employee</Th>
                            <Th>Department</Th>
                            <Th>Date</Th>
                            <Th>Hours Requested</Th>
                            <Th>Status</Th>
                            <Th>Submitted</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredReports.map((report, index) => (
                            <Tr key={index}>
                              <Td>
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="600">{report.employee_name}</Text>
                                  <Text fontSize="sm" color="gray.500">{report.employee_id}</Text>
                                </VStack>
                              </Td>
                              <Td>
                                <Text fontSize="sm">N/A</Text>
                              </Td>
                              <Td>
                                <Text fontSize="sm" fontWeight="500">
                                  {new Date(report.date).toLocaleDateString()}
                                </Text>
                              </Td>
                              <Td>
                                <Text fontWeight="600" color="blue.600">
                                  {overtimeManagementUtils.formatOvertimeHours(report.hours_requested)}
                                </Text>
                              </Td>
                              <Td>
                                {getStatusBadge(report.status)}
                              </Td>
                              <Td>
                                <Text fontSize="sm" color="gray.500">
                                  {new Date(report.created_at).toLocaleDateString()}
                                </Text>
                              </Td>
                              <Td>
                                <Button size="sm" variant="ghost" colorScheme="blue">
                                  View Details
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </VStack>
              </TabPanel>

              {/* Monthly Reports Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Text fontSize="lg" fontWeight="600" color="gray.800">
                      Monthly Overtime Reports
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        leftIcon={<FiRefreshCw />}
                        variant="outline"
                        size="sm"
                        onClick={refetchRequests}
                        isLoading={requestsIsLoading}
                      >
                        Refresh
                      </Button>
                      <Button
                        leftIcon={<FiDownload />}
                        variant="outline"
                        size="sm"
                        onClick={handleExport}
                      >
                        Export
                      </Button>
                    </HStack>
                  </Flex>

                  {/* Monthly Filters */}
                  <HStack spacing={4} wrap="wrap">
                    <Select
                      value={filters.month}
                      onChange={(e) => handleFilterChange('month', parseInt(e.target.value))}
                      minW="150px"
                    >
                      <option value={1}>January</option>
                      <option value={2}>February</option>
                      <option value={3}>March</option>
                      <option value={4}>April</option>
                      <option value={5}>May</option>
                      <option value={6}>June</option>
                      <option value={7}>July</option>
                      <option value={8}>August</option>
                      <option value={9}>September</option>
                      <option value={10}>October</option>
                      <option value={11}>November</option>
                      <option value={12}>December</option>
                    </Select>

                    <Select
                      value={filters.year}
                      onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                      minW="120px"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </Select>
                  </HStack>

                  {/* Monthly Summary */}
                  <Box textAlign="center" py={8}>
                    <Text fontSize="lg" color="gray.500">
                      Monthly reports feature coming soon
                    </Text>
                    <Text fontSize="sm" color="gray.400" mt={2}>
                      This will show detailed monthly overtime summaries and trends
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default OvertimeReports; 